import { go } from "@codemirror/lang-go";
import { java } from "@codemirror/lang-java";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { StreamLanguage } from "@codemirror/language";
import { csharp } from "@codemirror/legacy-modes/mode/clike";
import { EditorState, Extension, StateEffect } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { basicSetup } from "codemirror";
import * as React from "react";

import "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/codemirror.scss";
import { buildFireboltSqlExtensions } from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/dialect/dialect";

// C# language support using legacy mode (no @codemirror/lang-csharp package exists yet)
const csharpLanguage = StreamLanguage.define(csharp);

type ReadOnlyEditorLanguage =
  | "sql"
  | "javascript"
  | "python"
  | "java"
  | "go"
  | "csharp"
  | "json"
  | "text";

interface ReadOnlyEditorProps {
  value: string;
  className?: string;
  testId?: string;
  language?: ReadOnlyEditorLanguage;
}

const createExtensions = (language: ReadOnlyEditorLanguage): Extension[] => {
  const extensions: Extension[] = [
    EditorState.readOnly.of(true),
    EditorView.editable.of(false),
    basicSetup,
  ];

  switch (language) {
    case "javascript":
      extensions.push(javascript());
      break;
    case "python":
      extensions.push(python());
      break;
    case "java":
      extensions.push(java());
      break;
    case "go":
      extensions.push(go());
      break;
    case "json":
      extensions.push(json());
      break;
    case "csharp":
      extensions.push(csharpLanguage);
      break;
    case "text":
      break;
    case "sql":
    default:
      extensions.push(...buildFireboltSqlExtensions());
      break;
  }

  return extensions;
};

const ReadOnlyEditor = (props: ReadOnlyEditorProps) => {
  const { value, className, testId, language = "sql" } = props;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const viewRef = React.useRef<EditorView | null>(null);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    const view = new EditorView({
      state: EditorState.create({
        doc: value,
        extensions: createExtensions(language),
      }),
      parent: node,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [language, value]);

  React.useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }
    const currentValue = view.state.doc.toString();
    if (currentValue === value) {
      return;
    }
    view.dispatch({
      changes: {
        from: 0,
        to: currentValue.length,
        insert: value,
      },
    });
  }, [value]);

  React.useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    view.dispatch({
      effects: StateEffect.reconfigure.of(createExtensions(language)),
    });
  }, [language]);

  return (
    <div
      style={{ width: "100%", height: "100%" }}
      ref={containerRef}
      className={className}
      data-testid={testId}
    />
  );
};

export type { ReadOnlyEditorLanguage };
export default ReadOnlyEditor;
